import React from 'react'
import { Button } from '@/components/ui/button'
import { Bell, BellOff, Loader2 } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface BulkNotificationActionsProps {
  clientIds: string[]
  clientNames: string[]
  onBulkUpdate: (clientIds: string[], enabled: boolean) => Promise<boolean>
  disabled?: boolean
}

export function BulkNotificationActions({ 
  clientIds, 
  clientNames,
  onBulkUpdate,
  disabled 
}: BulkNotificationActionsProps) {
  const [showEnableDialog, setShowEnableDialog] = React.useState(false)
  const [showDisableDialog, setShowDisableDialog] = React.useState(false)
  const [isUpdating, setIsUpdating] = React.useState(false)

  const handleEnableAll = async () => {
    setIsUpdating(true)
    await onBulkUpdate(clientIds, true)
    setIsUpdating(false)
    setShowEnableDialog(false)
  }

  const handleDisableAll = async () => {
    setIsUpdating(true)
    await onBulkUpdate(clientIds, false)
    setIsUpdating(false)
    setShowDisableDialog(false)
  }

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowEnableDialog(true)}
        disabled={disabled || clientIds.length === 0 || isUpdating}
        className="gap-2"
      >
        {isUpdating ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Bell className="h-4 w-4" />
        )}
        Enable All
      </Button>

      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowDisableDialog(true)}
        disabled={disabled || clientIds.length === 0 || isUpdating}
        className="gap-2"
      >
        {isUpdating ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <BellOff className="h-4 w-4" />
        )}
        Disable All
      </Button>

      {/* Enable All Confirmation Dialog */}
      <AlertDialog open={showEnableDialog} onOpenChange={setShowEnableDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enable Notifications for All Clients?</AlertDialogTitle>
            <AlertDialogDescription>
              This will enable real-time Slack notifications (Positive, Negative, and Neutral) for{' '}
              <strong>{clientIds.length} client(s)</strong>:
              <div className="mt-3 max-h-40 overflow-y-auto">
                <ul className="list-disc list-inside space-y-1 text-sm">
                  {clientNames.slice(0, 10).map((name, idx) => (
                    <li key={idx}>{name}</li>
                  ))}
                  {clientNames.length > 10 && (
                    <li className="text-muted-foreground">
                      ...and {clientNames.length - 10} more
                    </li>
                  )}
                </ul>
              </div>
              <p className="mt-3 text-amber-600 dark:text-amber-500 font-medium">
                ⚠️ Clients without Slack channels configured will be skipped.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUpdating}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleEnableAll}
              disabled={isUpdating}
              className="bg-green-600 hover:bg-green-700"
            >
              {isUpdating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enabling...
                </>
              ) : (
                'Enable All'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Disable All Confirmation Dialog */}
      <AlertDialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable Notifications for All Clients?</AlertDialogTitle>
            <AlertDialogDescription>
              This will disable all real-time Slack notifications for{' '}
              <strong>{clientIds.length} client(s)</strong>:
              <div className="mt-3 max-h-40 overflow-y-auto">
                <ul className="list-disc list-inside space-y-1 text-sm">
                  {clientNames.slice(0, 10).map((name, idx) => (
                    <li key={idx}>{name}</li>
                  ))}
                  {clientNames.length > 10 && (
                    <li className="text-muted-foreground">
                      ...and {clientNames.length - 10} more
                    </li>
                  )}
                </ul>
              </div>
              <p className="mt-3 text-muted-foreground">
                You can re-enable notifications at any time.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUpdating}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDisableAll}
              disabled={isUpdating}
              className="bg-red-600 hover:bg-red-700"
            >
              {isUpdating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Disabling...
                </>
              ) : (
                'Disable All'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

